// Tabs, the data table, search box and filter selects (prototype 05-ui.js tabs(), table(), searchBox(), fsel()).
import { type ReactNode, useState } from 'react';
import { L } from '../lib/prefs';
import { ROLE_HUE } from './kit';
import { Empty, Ic } from './kit';
import { roleName } from '../lib/names';

export function Tabs<K extends string>({ items, cur, onPick, tone, cls }: { items: [K, string, number?][]; cur: K; onPick: (k: K) => void; tone?: string; cls?: string }) {
  return (
    <div className={'tabs' + (cls ? ' ' + cls : '')} role="tablist">
      {items.map(([k, label, n]) => (
        <button key={k} type="button" role="tab" aria-selected={k === cur} className="tab" onClick={() => onPick(k)}>
          {label}{n != null ? <b className={'n ' + (tone && n ? tone : '')}>{n}</b> : null}
        </button>
      ))}
    </div>
  );
}

export type Col<R> = { k: string; label: ReactNode; html: (r: R) => ReactNode; cls?: string; num?: boolean; sort?: (r: R) => string | number };
export function Table<R>({ rows: all, cols, rowKey, empty, cls }: { rows: R[]; cols: Col<R>[]; rowKey: (r: R) => string; empty?: ReactNode; cls?: string }) {
  const [sort, setSort] = useState<{ k: string; dir: number } | null>(null);
  const sc = sort && cols.find(c => c.k === sort.k);
  const rows = sc?.sort ? all.slice().sort((a, b) => { const x = sc.sort!(a), y = sc.sort!(b); return (x < y ? -1 : x > y ? 1 : 0) * sort!.dir; }) : all;
  const head = (c: Col<R>) => c.sort ? (
    <button type="button" className={'th-sort ' + (sort?.k === c.k ? 'on' : '')} onClick={() => setSort(sort?.k === c.k ? { k: c.k, dir: -sort.dir } : { k: c.k, dir: -1 })}>
      {c.label}<Ic n={sort?.k === c.k ? (sort.dir > 0 ? 'up' : 'down') : 'chevD'} cls="sm" />
    </button>
  ) : c.label;
  if (!rows.length) return <div className="t-empty">{empty || <Empty icon="search" title={L('Nothing matches', 'لا توجد نتائج')} body={L('Try a different filter or search.', 'جرّب عامل تصفية أو بحثاً مختلفاً.')} />}</div>;
  return (
    <>
      <div className="t-wrap">
        <table className={'tbl ' + (cls || '')}>
          <thead><tr>{cols.map(c => <th key={c.k} className={[c.cls, c.num && 'num'].filter(Boolean).join(' ')}>{head(c)}</th>)}</tr></thead>
          <tbody>{rows.map(r => <tr key={rowKey(r)}>{cols.map(c => <td key={c.k} className={[c.cls, c.num && 'num'].filter(Boolean).join(' ')}>{c.html(r)}</td>)}</tr>)}</tbody>
        </table>
      </div>
      <div className="t-foot"><span>{L(rows.length + (rows.length === 1 ? ' row' : ' rows'), rows.length + ' صفوف')}</span></div>
    </>
  );
}

export function SearchBox({ id, value, onChange, ph }: { id: string; value: string; onChange: (v: string) => void; ph: string }) {
  return <label className="search"><Ic n="search" /><input id={'q-' + id} type="search" placeholder={ph} value={value} onChange={e => onChange(e.target.value)} autoComplete="off" aria-label={ph} /></label>;
}

export function FSel({ id, label, value, opts, onChange }: { id: string; label: string; value: string; opts: [string, string][]; onChange: (v: string) => void }) {
  return (
    <label className={'fsel ' + (value !== '' ? 'on' : '')}>
      <span>{label}</span>
      <select id={id} value={value} onChange={e => onChange(e.target.value)} aria-label={label}>
        <option value="">{L('All', 'الكل')}</option>
        {opts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </label>
  );
}

export const Pill = ({ children, tone = '', title }: { children: ReactNode; tone?: string; title?: string }) => <span className={'pill ' + tone} title={title}><i />{children}</span>;
export const RolePill = ({ r }: { r: string }) => <span className="role" style={{ ['--h' as string]: ROLE_HUE[r] }}>{roleName(r)}</span>;
